import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  type Identifier,
  type useTranslate,
} from "ra-core";

import type { CrmDataProvider } from "../providers/types";
import type {
  SendWhatsAppMessageInput,
  SendWhatsAppMessageResult,
  WhatsAppMessagingCapability,
  WhatsAppSender,
  WhatsAppSenderCatalog,
  WhatsAppTemplate,
} from "../types";
import {
  initialSenderKey,
  selectableSenders,
  senderCanSend,
} from "../whatsapp/whatsappSenderSelection";

/** Query-key prefix for sender catalog / capability probes (never persisted). */
export const WHATSAPP_MESSAGING_QUERY_PREFIX = "whatsapp-messaging";

export const MAX_WHATSAPP_TEXT_LENGTH = 4096;

export type SendWhatsAppOptions = {
  /** Keep the inbox episode open instead of auto-resolving it on send. */
  keepOpen?: boolean;
};

export type SendWhatsAppOutcome =
  | {
      status: "sent";
      result: SendWhatsAppMessageResult;
      idempotencyKey: string;
      sentAt: string;
      body: string | null;
    }
  | { status: "blocked"; capability: WhatsAppMessagingCapability }
  | { status: "failed"; error: unknown };

type SendPayload = Omit<
  SendWhatsAppMessageInput,
  | "contact_id"
  | "sender_key"
  | "idempotency_key"
  | "operator_user_id"
  | "operator_name"
  | "keep_open"
>;

export function createIdempotencyKey() {
  if (
    typeof window !== "undefined" &&
    "crypto" in window &&
    "randomUUID" in window.crypto
  ) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function getWhatsAppErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "crm.whatsapp.error";
}

export function blockReasonText(
  reasons: string[],
  translate: ReturnType<typeof useTranslate>,
) {
  const first = reasons[0] || "unknown";
  return translate(`crm.whatsapp.block_reasons.${first}`, {
    _: translate("crm.whatsapp.block_reasons.unknown"),
  });
}

/**
 * Everything needed to send an operator WhatsApp message to one contact:
 * the sender catalog (with a ready default), the freeform-window capability for
 * the selected sender, operator identity, idempotency keys and error/notify
 * handling. Shared by the in-thread composer and the "Send WhatsApp" dialog.
 */
export function useSendWhatsAppMessage({
  contactId,
  enabled = true,
}: {
  contactId: Identifier;
  enabled?: boolean;
}) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const [requestedSenderKey, setSenderKey] = useState("");
  const [sending, setSending] = useState(false);

  const sendersQuery = useQuery({
    queryKey: [WHATSAPP_MESSAGING_QUERY_PREFIX, "senders"],
    queryFn: () => dataProvider.getWhatsAppSenders(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  const senders: WhatsAppSenderCatalog | null = sendersQuery.data ?? null;
  const selectable = useMemo(
    () => selectableSenders(senders, "direct"),
    [senders],
  );

  // Honour an explicit selection while it is still a ready sender; otherwise
  // fall back to the catalog default.
  const senderKey = useMemo(() => {
    if (!senders) return "";
    const requested = senders.senders.find(
      (sender) =>
        sender.sender_key === requestedSenderKey &&
        senderCanSend(sender, "direct"),
    );
    return requested?.sender_key ?? initialSenderKey(senders, "direct");
  }, [senders, requestedSenderKey]);

  const selectedSender: WhatsAppSender | undefined = useMemo(
    () => senders?.senders.find((sender) => sender.sender_key === senderKey),
    [senders, senderKey],
  );
  const senderReady = Boolean(
    selectedSender && senderCanSend(selectedSender, "direct"),
  );

  const capabilityKey = [
    WHATSAPP_MESSAGING_QUERY_PREFIX,
    "capability",
    String(contactId),
    senderKey,
  ];
  const capabilityQuery = useQuery({
    queryKey: capabilityKey,
    queryFn: () =>
      dataProvider.getWhatsAppMessagingCapability(
        contactId,
        senderKey || undefined,
      ),
    enabled: enabled && Boolean(senderKey),
    staleTime: 15_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });
  const capability = capabilityQuery.data ?? null;

  const sendMessage = useCallback(
    async (
      payload: SendPayload,
      { keepOpen }: SendWhatsAppOptions = {},
    ): Promise<SendWhatsAppOutcome> => {
      if (!selectedSender || !senderCanSend(selectedSender, "direct")) {
        notify("crm.whatsapp.inbox.composer.no_sender", { type: "warning" });
        return { status: "failed", error: new Error("no_ready_sender") };
      }
      setSending(true);
      try {
        if (payload.message_kind === "freeform_text") {
          // Re-check the 24h window right before sending: it may have closed
          // since the last capability probe.
          const fresh = await dataProvider.getWhatsAppMessagingCapability(
            contactId,
            selectedSender.sender_key,
          );
          queryClient.setQueryData(
            [
              WHATSAPP_MESSAGING_QUERY_PREFIX,
              "capability",
              String(contactId),
              selectedSender.sender_key,
            ],
            fresh,
          );
          if (!fresh.can_send_freeform) {
            notify("crm.whatsapp.requires_template_error", {
              type: "warning",
            });
            return { status: "blocked", capability: fresh };
          }
        }
        const idempotencyKey = createIdempotencyKey();
        const result = await dataProvider.sendWhatsAppMessage({
          ...payload,
          contact_id: contactId,
          sender_key: selectedSender.sender_key,
          idempotency_key: idempotencyKey,
          operator_user_id: identity?.id,
          operator_name: identity?.fullName,
          ...(keepOpen ? { keep_open: true } : {}),
        });
        notify("crm.whatsapp.sent", { type: "success" });
        void queryClient.invalidateQueries({
          queryKey: [
            WHATSAPP_MESSAGING_QUERY_PREFIX,
            "capability",
            String(contactId),
          ],
        });
        return {
          status: "sent",
          result,
          idempotencyKey,
          sentAt: new Date().toISOString(),
          body: payload.body ?? null,
        };
      } catch (error) {
        notify(getWhatsAppErrorMessage(error), { type: "error" });
        return { status: "failed", error };
      } finally {
        setSending(false);
      }
    },
    [contactId, dataProvider, identity, notify, queryClient, selectedSender],
  );

  const sendText = useCallback(
    (body: string, options?: SendWhatsAppOptions) =>
      sendMessage(
        {
          message_kind: "freeform_text",
          body: body.trim(),
          preview_url: false,
        },
        options,
      ),
    [sendMessage],
  );

  const sendTemplate = useCallback(
    (
      {
        template,
        parameters,
      }: { template: WhatsAppTemplate; parameters: Record<string, string> },
      options?: SendWhatsAppOptions,
    ) =>
      sendMessage(
        {
          message_kind: "template",
          template_id: template.id,
          template_name: template.name,
          template_language: template.language,
          template_parameters: parameters,
        },
        options,
      ),
    [sendMessage],
  );

  const refresh = useCallback(
    () => Promise.all([sendersQuery.refetch(), capabilityQuery.refetch()]),
    [sendersQuery, capabilityQuery],
  );

  return {
    capability,
    capabilityLoading: capabilityQuery.isPending && capabilityQuery.isFetching,
    senders,
    selectableSenders: selectable,
    senderKey,
    setSenderKey,
    selectedSender,
    senderReady,
    canSendFreeform: capability?.can_send_freeform === true,
    blockReasons: capability?.block_reasons ?? [],
    loading: sendersQuery.isPending || capabilityQuery.isFetching,
    sending,
    sendText,
    sendTemplate,
    refresh,
  };
}

export type SendWhatsAppMessageController = ReturnType<
  typeof useSendWhatsAppMessage
>;
